// Lightweight TipTap/ProseMirror JSON -> Markdown serializer
// Handles common node types to produce usable Markdown for AI/grep/debugging.

function escapeText(text) {
  return String(text || '').replace(/\r?\n/g, '\n');
}

function escapeTableCell(text) {
  return String(text || '')
    .replace(/\|/g, '\\|')
    .replace(/\r?\n+/g, ' ')
    .trim();
}

function unescapeMarkdownText(text) {
  return String(text || '')
    .replace(/\\\|/g, '|')
    .replace(/\\`/g, '`')
    .replace(/\\\*/g, '*')
    .replace(/\\_/g, '_');
}

function parseInlineMarkdown(text = '') {
  const input = String(text || '');
  if (!input) return [];

  const nodes = [];
  const tokenRe = /(\*\*[^*]+\*\*|`[^`]+`|\*[^*]+\*)/g;
  let cursor = 0;
  let match;

  while ((match = tokenRe.exec(input)) !== null) {
    const start = match.index;
    const token = match[0];

    if (start > cursor) {
      const plain = input.slice(cursor, start);
      if (plain) nodes.push({ type: 'text', text: unescapeMarkdownText(plain) });
    }

    if (token.startsWith('**') && token.endsWith('**')) {
      const inner = token.slice(2, -2);
      if (inner) {
        nodes.push({
          type: 'text',
          text: unescapeMarkdownText(inner),
          marks: [{ type: 'bold' }]
        });
      }
    } else if (token.startsWith('`') && token.endsWith('`')) {
      const inner = token.slice(1, -1);
      if (inner) {
        nodes.push({
          type: 'text',
          text: unescapeMarkdownText(inner),
          marks: [{ type: 'code' }]
        });
      }
    } else if (token.startsWith('*') && token.endsWith('*')) {
      const inner = token.slice(1, -1);
      if (inner) {
        nodes.push({
          type: 'text',
          text: unescapeMarkdownText(inner),
          marks: [{ type: 'italic' }]
        });
      }
    }

    cursor = start + token.length;
  }

  if (cursor < input.length) {
    const tail = input.slice(cursor);
    if (tail) nodes.push({ type: 'text', text: unescapeMarkdownText(tail) });
  }

  return nodes.length > 0 ? nodes : [{ type: 'text', text: unescapeMarkdownText(input) }];
}

function parseTableRowCells(line = '') {
  let normalized = String(line || '').trim();
  if (normalized.startsWith('|')) normalized = normalized.slice(1);
  if (normalized.endsWith('|')) normalized = normalized.slice(0, -1);

  const cells = [];
  let current = '';
  let escaped = false;

  for (let i = 0; i < normalized.length; i += 1) {
    const ch = normalized[i];
    if (escaped) {
      current += ch;
      escaped = false;
      continue;
    }
    if (ch === '\\') {
      escaped = true;
      current += ch;
      continue;
    }
    if (ch === '|') {
      cells.push(current.trim());
      current = '';
      continue;
    }
    current += ch;
  }
  cells.push(current.trim());

  return cells;
}

function isTableDividerLine(line = '') {
  const normalized = String(line || '').trim();
  if (!normalized.includes('|')) return false;

  const cells = parseTableRowCells(normalized);
  if (!cells.length) return false;

  return cells.every((cell) => /^:?-{3,}:?$/.test(cell));
}

function tableCellNode(type, value) {
  const text = String(value || '').trim();
  return {
    type,
    content: [
      {
        type: 'paragraph',
        content: text ? parseInlineMarkdown(text) : []
      }
    ]
  };
}

function parseTableBlock(lines = []) {
  if (!Array.isArray(lines) || lines.length < 2) return null;
  const headerCells = parseTableRowCells(lines[0]);
  if (!headerCells.length || !isTableDividerLine(lines[1])) return null;

  const rows = [];
  rows.push({
    type: 'tableRow',
    content: headerCells.map(cell => tableCellNode('tableHeader', cell))
  });

  for (let i = 2; i < lines.length; i += 1) {
    const rowCells = parseTableRowCells(lines[i]);
    const padded = [...rowCells];
    while (padded.length < headerCells.length) padded.push('');
    rows.push({
      type: 'tableRow',
      content: padded.slice(0, headerCells.length).map(cell => tableCellNode('tableCell', cell))
    });
  }

  return {
    type: 'table',
    content: rows
  };
}

function paragraphNodeFromMarkdown(line = '') {
  const text = String(line || '').trim();
  return {
    type: 'paragraph',
    content: text ? parseInlineMarkdown(text) : []
  };
}

function headingNodeFromMarkdown(line = '') {
  const match = String(line || '').match(/^(#{1,6})\s+(.+)$/);
  if (!match) return null;
  return {
    type: 'heading',
    attrs: { level: Math.max(1, Math.min(6, match[1].length)) },
    content: parseInlineMarkdown(match[2])
  };
}

function splitMarkdownBlocks(markdown = '') {
  const lines = String(markdown || '').replace(/\r\n?/g, '\n').split('\n');
  const blocks = [];
  let current = [];

  const flush = () => {
    if (current.length > 0) {
      blocks.push(current);
      current = [];
    }
  };

  for (const line of lines) {
    if (!line.trim()) {
      flush();
      continue;
    }
    current.push(line);
  }
  flush();

  return blocks;
}

const BULLET_LIST_RE = /^[-*•]\s+(.+)$/;
const ORDERED_LIST_RE = /^\d+[.)]\s+(.+)$/;

function bulletListNodeFromLines(lines) {
  return {
    type: 'bulletList',
    content: lines.map(line => ({
      type: 'listItem',
      content: [{
        type: 'paragraph',
        content: parseInlineMarkdown(line.trim().replace(BULLET_LIST_RE, '$1'))
      }]
    }))
  };
}

function orderedListNodeFromLines(lines) {
  return {
    type: 'orderedList',
    content: lines.map(line => ({
      type: 'listItem',
      content: [{
        type: 'paragraph',
        content: parseInlineMarkdown(line.trim().replace(ORDERED_LIST_RE, '$1'))
      }]
    }))
  };
}

export function markdownToNodes(markdown = '') {
  const blocks = splitMarkdownBlocks(markdown);
  const nodes = [];

  for (const blockLines of blocks) {
    // Table detection
    const looksLikeTable = blockLines.length >= 2 && blockLines.every(line => line.includes('|'));
    if (looksLikeTable) {
      const table = parseTableBlock(blockLines);
      if (table) {
        nodes.push(table);
        continue;
      }
    }

    // Bullet list: all lines in block must match
    if (blockLines.every(l => BULLET_LIST_RE.test(l.trim()))) {
      nodes.push(bulletListNodeFromLines(blockLines));
      continue;
    }

    // Ordered list: all lines in block must match
    if (blockLines.every(l => ORDERED_LIST_RE.test(l.trim()))) {
      nodes.push(orderedListNodeFromLines(blockLines));
      continue;
    }

    for (const line of blockLines) {
      const heading = headingNodeFromMarkdown(line);
      if (heading) {
        nodes.push(heading);
      } else {
        nodes.push(paragraphNodeFromMarkdown(line));
      }
    }
  }

  return nodes.length > 0 ? nodes : [{ type: 'paragraph' }];
}

export function markdownToDoc(markdown = '') {
  return {
    type: 'doc',
    content: markdownToNodes(markdown)
  };
}

function renderMarks(text, marks) {
  if (!marks || marks.length === 0) return escapeText(text);
  let out = escapeText(text);
  for (const m of marks) {
    if (m.type === 'bold' || m.type === 'strong') out = `**${out}**`;
    if (m.type === 'italic' || m.type === 'em') out = `*${out}*`;
    if (m.type === 'code') out = `\`${out}\``;
    if (m.type === 'link' && m.attrs?.href) out = `[${out}](${m.attrs.href})`;
  }
  return out;
}

function collectCellContent(node) {
  if (!node) return '';

  if (node.type === 'text') {
    return renderMarks(node.text || '', node.marks);
  }

  if (!Array.isArray(node.content)) {
    return '';
  }

  return node.content.map(collectCellContent).join('');
}

function renderTable(node) {
  const rows = Array.isArray(node?.content)
    ? node.content.filter((child) => child?.type === 'tableRow')
    : [];

  if (rows.length === 0) {
    return '';
  }

  const parsedRows = rows.map((row) => {
    const cells = Array.isArray(row?.content)
      ? row.content.filter((cell) => cell?.type === 'tableCell' || cell?.type === 'tableHeader')
      : [];

    return cells.map((cell) => escapeTableCell(collectCellContent(cell)));
  });

  const columnCount = Math.max(1, ...parsedRows.map((row) => row.length));
  const normalizedRows = parsedRows.map((row) => {
    const next = [...row];
    while (next.length < columnCount) next.push('');
    return next;
  });

  const headerCells = normalizedRows[0];
  const bodyRows = normalizedRows.slice(1);

  const headerLine = `| ${headerCells.join(' | ')} |`;
  const separatorLine = `| ${new Array(columnCount).fill('---').join(' | ')} |`;
  const bodyLines = bodyRows.map((row) => `| ${row.join(' | ')} |`);

  return [headerLine, separatorLine, ...bodyLines].join('\n') + '\n\n';
}

function toMarkdownBlockquote(text) {
  const lines = String(text || '').split('\n');
  if (lines.length === 0) return '>\n';
  return `${lines.map(line => (line ? `> ${line}` : '>')).join('\n')}\n`;
}

function renderChatMessage(node) {
  const role = String(node?.attrs?.role || '').trim().toLowerCase();
  const body = (node.content || []).map(nodeToMarkdown).join('').trim();

  if (role === 'user') {
    const quotedPrompt = toMarkdownBlockquote(body);
    return `@prompt:\n${quotedPrompt}\n`;
  }

  if (role === 'ai' || role === 'assistant') {
    const aiBody = body || '...';
    return `@ai:\n${aiBody}\n\n`;
  }

  if (role) {
    return `@${role}:\n${body || '...'}\n\n`;
  }

  return `${body}\n\n`;
}

function nodeToMarkdown(node) {
  if (!node) return '';

  if (node.type === 'text') {
    return renderMarks(node.text || '', node.marks);
  }

  const content = (node.content || []).map(nodeToMarkdown).join('');

  switch (node.type) {
    case 'paragraph':
      return content + "\n\n";
    case 'heading':
      const level = (node.attrs && node.attrs.level) || 1;
      return `${'#'.repeat(level)} ${content.trim()}\n\n`;
    case 'bulletList':
      return (node.content || []).map(item => `- ${nodeToMarkdown(item.content?.[0] || item).trim()}\n`).join('') + '\n';
    case 'orderedList':
      return (node.content || []).map((item, idx) => `${idx+1}. ${nodeToMarkdown(item.content?.[0] || item).trim()}\n`).join('') + '\n';
    case 'listItem':
      return content;
    case 'codeBlock':
      return `\n\n\`\`\`\n${escapeText(node.content?.map(n => n.text).join('') || '')}\n\`\`\`\n\n`;
    case 'blockquote':
      return content.split('\n').map(line => line ? `> ${line}` : '').join('\n') + '\n\n';
    case 'horizontalRule':
      return '---\n\n';
    case 'image':
      return `![${node.attrs?.alt || ''}](${node.attrs?.src || ''})\n\n`;
    case 'chatMessage':
      return renderChatMessage(node);
    case 'table':
      return renderTable(node);
    case 'tableRow':
    case 'tableCell':
    case 'tableHeader':
      return content;
    case 'hardBreak':
      return '  \n';
    case 'doc':
      return (node.content || []).map(nodeToMarkdown).join('').trim() + '\n';
    default:
      return content;
  }
}

export function serializeToMarkdown(docOrContent) {
  if (!docOrContent) return '';
  // Accept either a full doc or the content array
  const node = Array.isArray(docOrContent) ? { type: 'doc', content: docOrContent } : docOrContent;
  try {
    return nodeToMarkdown(node).trim();
  } catch (err) {
    return '';
  }
}

export default serializeToMarkdown;
